import React from 'react'
import { Area, ComposedChart, Line, ResponsiveContainer, Tooltip } from 'recharts'

import { CartesianGrid } from '../../../components/chart/CartesianGrid'
import { XAxis } from '../../../components/chart/XAxis'
import { YAxis } from '../../../components/chart/YAxis'
import { formatMonth, shortFormatNumber } from '../../../util'
import { EarningsEstimateType } from '../../earnings-common/types'
import { getMonthlyTicks } from '../../earnings-common/utils'
import {
  EarningsColor,
  PRICE_REVISIONS_CHART_HEIGHT,
  PRICE_REVISIONS_TICK_COUNT,
  PRICE_REVISIONS_YAXIS_WIDTH,
} from '../constants'
import { RevisionsChartData } from '../types'
import { getEarningsColor } from '../utils'
import { PriceRevisionTooltip } from './PriceRevisionTooltip'

type Props = {
  activeIndex: number
  chartData: RevisionsChartData
  endingTimestamp: number
  estimateType: EarningsEstimateType
  isActive: boolean
  onSetActiveIndex: (value: number) => void
  onSetIsActive: (value: boolean) => void
  startingTimestamp: number
}

export function PriceRevisionsChart({
  activeIndex,
  chartData,
  endingTimestamp,
  estimateType,
  isActive,
  onSetActiveIndex,
  onSetIsActive,
  startingTimestamp,
}: Props) {
  const monthlyTicks = React.useMemo(
    () => getMonthlyTicks(startingTimestamp, endingTimestamp),
    [startingTimestamp, endingTimestamp]
  )

  return (
    <div className="w-full">
      <div
        /**
         * Fix a problem with chart tooltip not hiding correctly on mouse leave
         * See: https://github.com/recharts/recharts/issues/2726
         */
        onMouseEnter={() => {
          onSetIsActive(true)
        }}
        onMouseLeave={() => {
          onSetIsActive(false)
        }}
      >
        <ResponsiveContainer height={PRICE_REVISIONS_CHART_HEIGHT}>
          <ComposedChart
            data={chartData}
            syncId="estimateRevisions"
            margin={{ left: 10, right: 4, top: 0, bottom: 0 }}
            onMouseMove={(props) => {
              if (typeof props.activeTooltipIndex === 'number' && props.activeTooltipIndex !== activeIndex) {
                onSetActiveIndex(props.activeTooltipIndex)
              }
            }}
            onMouseLeave={() => {
              onSetActiveIndex(-1)
            }}
          >
            <CartesianGrid vertical />
            <XAxis
              axisLine={{ stroke: getEarningsColor(EarningsColor.axis) }}
              dataKey="timestamp"
              domain={[startingTimestamp, endingTimestamp]}
              scale="time"
              tickFormatter={(value: number) => formatMonth(value)}
              ticks={monthlyTicks}
              type="number"
            />
            <YAxis
              domain={['auto', 'auto']}
              interval="preserveStartEnd"
              tickCount={PRICE_REVISIONS_TICK_COUNT}
              tickFormatter={(value) => shortFormatNumber(value)}
              width={PRICE_REVISIONS_YAXIS_WIDTH}
              yAxisId="estimate"
            />
            <YAxis
              domain={['auto', 'auto']}
              interval="preserveStartEnd"
              orientation="right"
              tickCount={PRICE_REVISIONS_TICK_COUNT}
              width={PRICE_REVISIONS_YAXIS_WIDTH}
              yAxisId="price"
            />
            <Area
              activeDot={false}
              connectNulls
              dataKey="lowHigh"
              dot={false}
              fill={getEarningsColor(EarningsColor.lowHigh)}
              fillOpacity={0.12}
              isAnimationActive={false}
              stroke={getEarningsColor(EarningsColor.lowHigh)}
              type="linear"
              yAxisId="estimate"
            ></Area>
            <Line
              activeDot={false}
              connectNulls={false}
              dataKey="price"
              dot={false}
              isAnimationActive={false}
              stroke={getEarningsColor(EarningsColor.price)}
              type="linear"
              yAxisId="price"
            ></Line>
            <Line
              activeDot={false}
              connectNulls={false}
              dataKey="mean"
              dot={false}
              isAnimationActive={false}
              stroke={getEarningsColor(EarningsColor.estimate)}
              type="linear"
              yAxisId="estimate"
            ></Line>
            <Tooltip
              content={() => (
                <PriceRevisionTooltip
                  estimateType={estimateType}
                  isActive={isActive}
                  revision={chartData[activeIndex]}
                />
              )}
              cursor={isActive ? { stroke: getEarningsColor(EarningsColor.cursor) } : false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
